import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
  ChartFiltersWiseconn,
} from '../../types';
import { calculateSeries, prepareData } from '../builder';

import { PH_ELECTRICITY_NAME, CHART_TITLE_PH_CONDUCTIVIDAD } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import { FormattedSeries } from '../types';
import {
  getChartConfig,
  getYAxis,
  calculateYAxisLimits,
  getPhElectricityTooltip,
} from '../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import PhElectricityConfigContent from './PhElectricityConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

type BasicSeries = {
  type: string;
  name: string;
  data: { x: number; y: number; custom?: unknown }[];
  zIndex?: number;
  yAxis?: number;
};

function PhElectricityChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  hideRightYAxisTitle = false,
  height = 600,
  title = CHART_TITLE_PH_CONDUCTIVIDAD,
  yAxisTitle = 'Temperatura (ºC)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  hideRightYAxisTitle?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
}) {
  const chartName = 'ph-electricity';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const transformData = useCallback(
    (
      preparedData: FormattedSeries[],
    ) => {
      const seriesData = calculateSeries(
        preparedData,
        true,
        interval?.startDate || '',
        interval?.endDate || '',
      );

      const mappedSeries: BasicSeries[] = (
        (seriesData.series as unknown) as BasicSeries[] || []
      ).map((s: BasicSeries) => {
        const name: string = s?.name || '';
        if (name.includes('Irrigation Electrical Conductivity')) {
          return { ...s, yAxis: 0, name: 'Conductividad de suelo (mS/cm)' };
        }
        if (name.includes('pH')) {
          return { ...s, yAxis: 1, name: 'pH' };
        }
        return s;
      });

      setChartData({
        ...seriesData,
        series: mappedSeries,
      });
    }, [interval?.startDate, interval?.endDate],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      if (interval) {
        const chartFilters: ChartFiltersWiseconn = {
          farmId,
          sectorId,
          startDate: interval?.startDate || '',
          endDate: interval?.endDate || '',
          chart: PH_ELECTRICITY_NAME,
        };

        const [pHPreparedData, irrigationElectricalConductivityPreparedData] = await Promise.all([
          prepareData({
            ...chartFilters,
            sensorType: 'pH',
            telemetryType: selectedSector?.telemetryType || 'wiseconn',
            sn: selectedSector?.sn || '',
          }, selectedSector?.telemetryType),
          prepareData({
            ...chartFilters,
            sensorType: 'Irrigation Electrical Conductivity',
            telemetryType: selectedSector?.telemetryType || 'wiseconn',
            sn: selectedSector?.sn || '',
          }, selectedSector?.telemetryType),
        ]);

        const combinedPreparedData = [
          ...pHPreparedData,
          ...irrigationElectricalConductivityPreparedData,
        ];

        transformData(combinedPreparedData);
      }
    } catch (error) {
      enqueueSnackbar('Error fetching data', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    transformData,
    selectedSector,
    sectorId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getPhElectricityTooltip(this, conversations);
    },
  }), [conversations]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const pHSeries = ((chartData.series as unknown) as BasicSeries[]).filter((series: BasicSeries) => series.yAxis === 1 || series.name?.includes('pH'));
    const irrigationElectricalConductivitySeries = ((chartData.series as unknown) as BasicSeries[]).filter((series: BasicSeries) => series.yAxis === 0 || series.name?.includes('Irrigation Electrical Conductivity'));
    const pHLimits = calculateYAxisLimits(pHSeries);
    const irrigationElectricalConductivityLimits = calculateYAxisLimits(
      irrigationElectricalConductivitySeries,
    );

    const yAxisConfig: Highcharts.YAxisOptions[] = [
      {
        ...getYAxis(hideRightYAxisTitle ? '' : 'Conductividad de suelo (mS/cm)', hideYValues || hideRightYAxisTitle),
        id: 'irrigationElectricalConductivity',
        opposite: true,
        min: irrigationElectricalConductivityLimits.min,
        max: irrigationElectricalConductivityLimits.max,
      } as Highcharts.YAxisOptions,
      {
        ...getYAxis('pH', hideYValues),
        id: 'pH',
        min: pHLimits.min,
        max: pHLimits.max,
      } as Highcharts.YAxisOptions,
    ];

    return ({
      ...getChartConfig(
        chartData,
        height,
        hideXValues,
        undefined,
        dayjs(interval.startDate).valueOf(),
        dayjs(interval.endDate).valueOf(),
      ),
      yAxis: yAxisConfig,
      plotOptions: {
        series: {
          marker: {
            enabled: true,
          },
        },
      },
    });
  }, [
    chartData,
    height,
    hideYValues,
    hideRightYAxisTitle,
    interval?.startDate,
    interval?.endDate,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            PH_ELECTRICITY_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div>
        <BaseHighchart
          chartView={chartView}
          sectorId={sectorId}
          filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
          title={title}
          yAxisTitle={yAxisTitle}
          series={chartData.series as Highcharts.SeriesOptionsType[]}
          tooltip={customTooltip}
          hideYValues={hideYValues}
          adjustYWithLegend={true}
          showFullScreen={true}
          legend={{ enabled: !hideLegend }}
          chartConfig={chartConfig}
          onConfigClick={handleConfigClick}
          onFullScreenClick={handleFullScreenClick}
          absoluteCategories={chartData.absoluteCategories}
          setModalState={setModalState}
          conversations={conversations}
          height={height}
          zoomSync={true}
          autoAdjustYAxis={true}
          interval={interval}
          graphName={PH_ELECTRICITY_NAME}
        />
      </div>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={PH_ELECTRICITY_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · pH y Conductividad"
      >
        <PhElectricityConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default PhElectricityChart;
